import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';

const getToken = () => localStorage.getItem('token');
const authHeader = () => ({ Authorization: `Bearer ${getToken()}` });

const DATA_SOURCES = ['Facebook', 'Instagram', 'OLX', 'Twitter', 'Institution', 'Website', 'Google', 'Whatsapp', 'Other'];

const SOURCE_COLORS = {
  Facebook:    'bg-blue-100 text-blue-700 border-blue-300',
  Instagram:   'bg-pink-100 text-pink-700 border-pink-300',
  OLX:         'bg-teal-100 text-teal-700 border-teal-300',
  Twitter:     'bg-sky-100 text-sky-700 border-sky-300',
  Institution: 'bg-purple-100 text-purple-700 border-purple-300',
  Website:     'bg-indigo-100 text-indigo-700 border-indigo-300',
  Google:      'bg-yellow-100 text-yellow-700 border-yellow-300',
  Whatsapp:    'bg-green-100 text-green-700 border-green-300',
  Other:       'bg-gray-100 text-gray-600 border-gray-300',
};

const SourceBadge = ({ source }) => (
  <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-bold ${SOURCE_COLORS[source] || SOURCE_COLORS.Other}`}>
    {source}
  </span>
);

const fmtDateTime = (dt) => {
  if (!dt) return '—';
  try {
    return new Date(dt).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch {
    return dt;
  }
};

const EMPTY_FORM = { name: '', contact_number: '', data_from: '' };

const MarketingDashboard = ({ name, API_URL: propApiUrl }) => {
  const API_URL = propApiUrl || process.env.REACT_APP_API_URL || 'http://localhost:5000';

  const [leads, setLeads]               = useState([]);
  const [loading, setLoading]           = useState(true);
  const [search, setSearch]             = useState('');
  const [filterSource, setFilterSource] = useState('All');

  const [showModal, setShowModal]       = useState(false);
  const [editingLead, setEditingLead]   = useState(null);
  const [form, setForm]                 = useState(EMPTY_FORM);
  const [saving, setSaving]             = useState(false);
  const [formError, setFormError]       = useState('');

  const [deleteId, setDeleteId]         = useState(null);
  const [deleting, setDeleting]         = useState(false);

  const [forwardLead, setForwardLead]   = useState(null);
  const [hrList, setHrList]             = useState([]);
  const [selectedHr, setSelectedHr]     = useState('');
  const [forwarding, setForwarding]     = useState(false);
  const [forwardError, setForwardError] = useState('');

  const fileRef = useRef(null);
  const [importing, setImporting]         = useState(false);
  const [importError, setImportError]     = useState('');
  const [importSuccess, setImportSuccess] = useState('');

  const [toast, setToast] = useState('');

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3000);
  };

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/staff/marketing/leads`, { headers: authHeader() });
      setLeads(res.data || []);
    } catch {
      setLeads([]);
    } finally {
      setLoading(false);
    }
  }, [API_URL]);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  const filtered = leads.filter(l => {
    const q = search.toLowerCase();
    const matchSearch = !q || l.name?.toLowerCase().includes(q) || l.contact_number?.includes(q);
    const matchSource = filterSource === 'All' || l.data_from === filterSource;
    return matchSearch && matchSource;
  });

  const counts = {
    All: leads.length,
    ...Object.fromEntries(DATA_SOURCES.map(s => [s, leads.filter(l => l.data_from === s).length]))
  };

  const openAdd = () => { setEditingLead(null); setForm(EMPTY_FORM); setFormError(''); setShowModal(true); };
  const openEdit = (lead) => {
    setEditingLead(lead);
    setForm({ name: lead.name, contact_number: lead.contact_number, data_from: lead.data_from });
    setFormError('');
    setShowModal(true);
  };

  const handleFormChange = (e) => setForm(p => ({ ...p, [e.target.name]: e.target.value }));

  const handleSave = async () => {
    if (!form.name.trim()) { setFormError('Name is required'); return; }
    if (!/^\d{10,13}$/.test(form.contact_number.trim())) { setFormError('Contact number must be 10 to 13 digits'); return; }
    if (!form.data_from) { setFormError('Please select a data source'); return; }
    setSaving(true); setFormError('');
    try {
      if (editingLead) {
        const res = await axios.put(`${API_URL}/api/staff/marketing/leads/${editingLead.id}`, form, { headers: authHeader() });
        setLeads(p => p.map(l => l.id === editingLead.id ? res.data.lead : l));
        showToast('Lead updated');
      } else {
        const res = await axios.post(`${API_URL}/api/staff/marketing/leads`, form, { headers: authHeader() });
        setLeads(p => [res.data.lead, ...p]);
        showToast('Lead added');
      }
      setShowModal(false);
    } catch (err) {
      setFormError(err.response?.data?.error || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API_URL}/api/staff/marketing/leads/${deleteId}`, { headers: authHeader() });
      setLeads(p => p.filter(l => l.id !== deleteId));
      setDeleteId(null);
      showToast('Lead deleted');
    } catch {
      showToast('Failed to delete');
    } finally {
      setDeleting(false);
    }
  };

  const openForward = async (lead) => {
    setForwardLead(lead); setSelectedHr(''); setForwardError('');
    try {
      const res = await axios.get(`${API_URL}/api/staff/marketing/hr-executives`, { headers: authHeader() });
      setHrList(res.data || []);
    } catch {
      setHrList([]);
    }
  };

  const handleForward = async () => {
    if (!selectedHr) { setForwardError('Please select an HR Executive'); return; }
    setForwarding(true); setForwardError('');
    try {
      const res = await axios.post(
        `${API_URL}/api/staff/marketing/leads/${forwardLead.id}/forward`,
        { hr_user_id: selectedHr },
        { headers: authHeader() }
      );
      setLeads(p => p.map(l => l.id === forwardLead.id ? res.data.lead : l));
      setForwardLead(null);
      showToast('Lead forwarded successfully');
    } catch (err) {
      setForwardError(err.response?.data?.error || 'Failed to forward');
    } finally {
      setForwarding(false);
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true); setImportError(''); setImportSuccess('');
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await axios.post(`${API_URL}/api/staff/marketing/leads/import`, fd, {
        headers: { ...authHeader(), 'Content-Type': 'multipart/form-data' }
      });
      setImportSuccess(`Imported ${res.data.imported} leads`);
      fetchLeads();
    } catch (err) {
      const details = err.response?.data?.details;
      setImportError(details ? details.join(' | ') : (err.response?.data?.error || 'Import failed'));
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleTemplateDownload = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/staff/marketing/leads/template`, {
        headers: authHeader(), responseType: 'blob'
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = 'marketing-leads-template.xlsx'; a.click();
      URL.revokeObjectURL(url);
    } catch {
      showToast('Failed to download template');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      {toast && (
        <div className="fixed top-4 right-4 z-50 rounded-xl bg-black px-5 py-3 text-sm font-bold text-white shadow-xl">{toast}</div>
      )}

      <div className="mb-6">
        <h1 className="text-2xl font-black text-black">Marketing Dashboard</h1>
        <p className="text-sm text-gray-500">Welcome, {name}</p>
      </div>

      {/* Source summary cards */}
      <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        {DATA_SOURCES.map(s => (
          <button key={s} onClick={() => setFilterSource(p => p === s ? 'All' : s)}
            className={`rounded-2xl border p-3 text-left transition-all ${filterSource === s ? 'border-yellow-400 bg-yellow-50 ring-2 ring-yellow-300' : 'border-gray-200 bg-white hover:border-yellow-300'}`}>
            <div className="text-xl font-black text-black">{counts[s] || 0}</div>
            <div className="text-xs font-semibold text-gray-500">{s}</div>
          </button>
        ))}
      </div>

      {/* Toolbar */}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[180px]">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
          </svg>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search name or contact…"
            className="w-full rounded-2xl border border-gray-300 bg-white py-2.5 pl-9 pr-4 text-sm text-black outline-none focus:border-yellow-400 focus:ring-2 focus:ring-yellow-200" />
        </div>

        <select value={filterSource} onChange={e => setFilterSource(e.target.value)}
          className="rounded-2xl border border-gray-300 bg-white px-4 py-2.5 text-sm font-semibold text-black outline-none focus:border-yellow-400">
          <option value="All">All Sources ({counts.All})</option>
          {DATA_SOURCES.map(s => <option key={s} value={s}>{s} ({counts[s] || 0})</option>)}
        </select>

        <button onClick={openAdd}
          className="rounded-2xl bg-black px-5 py-2.5 text-sm font-bold text-yellow-400 hover:bg-gray-800 transition-colors">
          + Add Lead
        </button>

        <label className="cursor-pointer rounded-2xl border-2 border-dashed border-gray-300 px-4 py-2.5 text-sm font-semibold text-gray-600 hover:border-yellow-400 hover:text-black transition-colors">
          {importing ? 'Importing…' : 'Import Excel'}
          <input ref={fileRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={handleImport} disabled={importing} />
        </label>

        <button onClick={handleTemplateDownload}
          className="rounded-2xl border border-gray-300 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 hover:border-yellow-400 hover:text-black transition-colors">
          Template
        </button>
      </div>

      {importError   && <div className="mb-3 rounded-xl bg-red-50 border border-red-200 px-4 py-2 text-sm text-red-700">{importError}</div>}
      {importSuccess && <div className="mb-3 rounded-xl bg-green-50 border border-green-200 px-4 py-2 text-sm text-green-700">{importSuccess}</div>}

      {/* Table */}
      <div className="overflow-x-auto rounded-2xl border border-gray-200 bg-white shadow-sm">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="bg-black text-yellow-400">
              <th className="px-4 py-3 text-left font-bold">S.No</th>
              <th className="px-4 py-3 text-left font-bold">Name</th>
              <th className="px-4 py-3 text-left font-bold">Contact Number</th>
              <th className="px-4 py-3 text-left font-bold">Data From</th>
              <th className="px-4 py-3 text-left font-bold">Added On</th>
              <th className="px-4 py-3 text-left font-bold">Forwarded To</th>
              <th className="px-4 py-3 text-center font-bold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-gray-400">Loading…</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-gray-400">No leads found</td></tr>
            ) : filtered.map((lead, idx) => (
              <tr key={lead.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                <td className="px-4 py-3 font-semibold text-gray-500">{idx + 1}</td>
                <td className="px-4 py-3 font-bold text-black">{lead.name}</td>
                <td className="px-4 py-3 font-mono text-gray-700">{lead.contact_number}</td>
                <td className="px-4 py-3"><SourceBadge source={lead.data_from} /></td>
                <td className="px-4 py-3 text-gray-500 text-xs">{fmtDateTime(lead.created_at)}</td>
                <td className="px-4 py-3 text-xs text-gray-500">
                  {lead.forwarded_to_name
                    ? <span className="font-semibold text-green-700">{lead.forwarded_to_name}<br /><span className="text-gray-400 font-normal">{fmtDateTime(lead.forwarded_at)}</span></span>
                    : <span className="text-gray-300">—</span>}
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-center gap-2">
                    <button onClick={() => openEdit(lead)}
                      className="rounded-lg bg-yellow-400 px-3 py-1.5 text-xs font-bold text-black hover:bg-yellow-500 transition-colors">
                      Edit
                    </button>
                    <button onClick={() => openForward(lead)}
                      className="rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-blue-700 transition-colors">
                      Forward
                    </button>
                    <button onClick={() => setDeleteId(lead.id)}
                      className="rounded-lg bg-red-100 px-3 py-1.5 text-xs font-bold text-red-700 hover:bg-red-200 transition-colors">
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length > 0 && (
          <div className="border-t border-gray-100 px-4 py-2 text-xs text-gray-400 text-right">
            Showing {filtered.length} of {leads.length} leads
          </div>
        )}
      </div>

      {/* Add / Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-3xl bg-white p-6 shadow-2xl">
            <h2 className="mb-5 text-lg font-black text-black">{editingLead ? 'Edit Lead' : 'Add New Lead'}</h2>
            <div className="space-y-4">
              <label className="block">
                <span className="text-sm font-bold text-black">Full Name</span>
                <input name="name" value={form.name} onChange={handleFormChange} placeholder="Enter full name"
                  className="mt-1 w-full rounded-2xl border border-gray-300 bg-gray-50 px-4 py-3 text-black outline-none focus:border-yellow-400 focus:ring-2 focus:ring-yellow-200" />
              </label>
              <label className="block">
                <span className="text-sm font-bold text-black">Contact Number</span>
                <input name="contact_number" value={form.contact_number} onChange={handleFormChange}
                  placeholder="10-13 digit mobile number" maxLength={13}
                  className="mt-1 w-full rounded-2xl border border-gray-300 bg-gray-50 px-4 py-3 text-black outline-none focus:border-yellow-400 focus:ring-2 focus:ring-yellow-200" />
              </label>
              <label className="block">
                <span className="text-sm font-bold text-black">Data From</span>
                <select name="data_from" value={form.data_from} onChange={handleFormChange}
                  className="mt-1 w-full rounded-2xl border border-gray-300 bg-gray-50 px-4 py-3 text-black outline-none focus:border-yellow-400 focus:ring-2 focus:ring-yellow-200">
                  <option value="">Select source…</option>
                  {DATA_SOURCES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </label>
            </div>
            {formError && <p className="mt-3 text-sm text-red-600 font-semibold">{formError}</p>}
            <div className="mt-6 flex justify-end gap-3">
              <button onClick={() => setShowModal(false)}
                className="rounded-2xl border border-gray-300 px-5 py-2.5 text-sm font-bold text-gray-700 hover:bg-gray-50">
                Cancel
              </button>
              <button onClick={handleSave} disabled={saving}
                className="rounded-2xl bg-black px-6 py-2.5 text-sm font-bold text-yellow-400 hover:bg-gray-800 disabled:opacity-60">
                {saving ? 'Saving…' : (editingLead ? 'Update' : 'Save')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm Modal */}
      {deleteId && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-sm rounded-3xl bg-white p-6 shadow-2xl text-center">
            <div className="mb-3 text-4xl">🗑️</div>
            <h2 className="mb-2 text-lg font-black text-black">Delete Lead?</h2>
            <p className="mb-6 text-sm text-gray-500">This action cannot be undone.</p>
            <div className="flex justify-center gap-3">
              <button onClick={() => setDeleteId(null)}
                className="rounded-2xl border border-gray-300 px-5 py-2.5 text-sm font-bold text-gray-700 hover:bg-gray-50">
                Cancel
              </button>
              <button onClick={handleDelete} disabled={deleting}
                className="rounded-2xl bg-red-600 px-6 py-2.5 text-sm font-bold text-white hover:bg-red-700 disabled:opacity-60">
                {deleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Forward Modal */}
      {forwardLead && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-3xl bg-white p-6 shadow-2xl">
            <h2 className="mb-1 text-lg font-black text-black">Forward Lead</h2>
            <p className="mb-4 text-sm text-gray-500">
              Forward <span className="font-bold text-black">{forwardLead.name}</span> to an HR Executive
            </p>
            {hrList.length === 0 ? (
              <p className="text-sm text-gray-400">No HR Executives available</p>
            ) : (
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {hrList.map(hr => (
                  <label key={hr.user_id}
                    className={`flex cursor-pointer items-center gap-3 rounded-2xl border p-3 transition-all ${selectedHr === hr.user_id ? 'border-yellow-400 bg-yellow-50' : 'border-gray-200 hover:border-yellow-300'}`}>
                    <input type="radio" name="hr_select" value={hr.user_id} checked={selectedHr === hr.user_id}
                      onChange={() => setSelectedHr(hr.user_id)} className="accent-yellow-400" />
                    <span className="font-semibold text-black">{hr.name}</span>
                  </label>
                ))}
              </div>
            )}
            {forwardError && <p className="mt-3 text-sm text-red-600 font-semibold">{forwardError}</p>}
            <div className="mt-6 flex justify-end gap-3">
              <button onClick={() => setForwardLead(null)}
                className="rounded-2xl border border-gray-300 px-5 py-2.5 text-sm font-bold text-gray-700 hover:bg-gray-50">
                Cancel
              </button>
              <button onClick={handleForward} disabled={forwarding || hrList.length === 0}
                className="rounded-2xl bg-blue-600 px-6 py-2.5 text-sm font-bold text-white hover:bg-blue-700 disabled:opacity-60">
                {forwarding ? 'Forwarding…' : 'Forward'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MarketingDashboard;
